import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  TableContainer,
  Tooltip,
  Select,
  MenuItem,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import Cliploader from "./Cliploader";
import Swal from "sweetalert2";
import axios from "axios";

const ExcelEditor = forwardRef(
  (
    { materialesOrden, setMaterialesOrden, idOrder, errores, setErrores },
    ref,
  ) => {
    const [rows, setRows] = useState([]);
    const [catalogoProblms, setCatalogoProblems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = rows.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(rows.length / rowsPerPage);
    const columns = [
      "MATERIALES",
      "DESCRIPCIÓN DE MATERIAL",
      "PIEZAS PEDIDAS",
      "COSTO UNITARIO",
      "FACTURADOS",

      "NO APTOS A LA RECEPCIÓN",
      "FALTANTES FACTURADOS",
      "TOTALES A INGRESAR",

      "FALTANTES NO FACTURADOS",
      "PROBLEMAS EN RECEPCION",
      "DIAGNOSTICO",
      "TRATAMIENTO",
      "COMENTARIOS GENERALES",
      "ELIMINAR",
    ];
    const columns2 = [
      "materialId",
      "materialDescripcion",
      "piezasPedidas",
      "costoUnitario",
      "facturados",
      "noAptos",
      "faltantesFacturados",
      "cantidadRecibidos",
      "faltantesNoFacturados",
      "problemasEnRecepcion",
      "diagnostico",
      "tratamiento",
      "comentariosGenerales",
      "manual",
    ];

    /*console.log("MATERIALES ORDEN", materialesOrden);
    console.log("ROWS", rows);
    console.log("CURRENT ROWS", currentRows);
    console.log("COLUMNS", columns);
    console.log("CURRENT PAGE", currentPage);*/

    useEffect(() => {
      if (!materialesOrden || materialesOrden.length === 0) {
        setRows([]);
        return;
      }
      const mappedRows = materialesOrden.map((item) => createEmptyRow(item));
      setRows(mappedRows);
    }, [materialesOrden]);

    const createEmptyRow = (item) => ({
      materialId: item.materialId || "",
      materialDescripcion: item.materialDescripcion || "",
      piezasPedidas: item.piezasPedidas || "",
      costoUnitario: item.costoUnitario || "",
      facturados: item.facturados || "",
      cantidadRecibidos: item.cantidadRecibidos ?? "",
      noAptos: item.noAptos || "",
      faltantesFacturados: item.faltantesFacturados || "",
      faltantesNoFacturados: item.faltantesNoFacturados || "",
      problemasEnRecepcion: item.problemasEnRecepcion || "",
      diagnostico: item.diagnostico || "",
      tratamiento: item.tratamiento || "",
      comentariosGenerales: item.comentariosGenerales || "",
      manual: item.manual === true,
      addedNow: item.addedNow === true ? true : false,
    });

    const getProblemsCatalogo = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_PETCOZOO}/problematicas/getAllProblematicas`,
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_TOKEN}`,
              apikey: `${process.env.REACT_APP_APIKEY}`,
              "Content-Type": "application/json",
            },
          },
        );
        setCatalogoProblems(data.data);
      } catch (error) {
        console.log("Error al obtener problemas", error);
      }
    };

    useEffect(() => {
      getProblemsCatalogo();
    }, []);

    const handleChange = (indexInPage, field, value) => {
      // Calculamos el índice real en el array completo para no perder datos de otras páginas
      const globalIndex = indexOfFirstRow + indexInPage;

      const numericFields = [
        "materialId",
        "facturados",
        "cantidadRecibidos",
        "noAptos",
        "faltantesFacturados",
        "faltantesNoFacturados",
      ];
      const decimalFields = ["piezasPedidas", "costoUnitario"];

      if (numericFields.includes(field) && !/^$|^\d+$/.test(value)) return;
      if (decimalFields.includes(field) && !/^\d*\.?\d*$/.test(value)) return;

      const updated = [...rows];
      updated[globalIndex] = { ...updated[globalIndex], [field]: value };

      const newErrores = { ...errores };
      const row = updated[globalIndex];

      const cantidadRecibidosCalc =
        Number(row.facturados || 0) -
        (Number(row.noAptos || 0) + Number(row.faltantesFacturados || 0));

      if (cantidadRecibidosCalc < 0) {
        Swal.fire({
          icon: "warning",
          title: "Dato inválido",
          text: "El total a ingresar no puede ser negativo. Por favor revisa el valor ingresado.",
          confirmButtonColor: "#00205B",
          confirmButtonText: "Aceptar",
        });
        updated[globalIndex][field] = "";
        const recalculated =
          Number(updated[globalIndex].facturados || 0) -
          (Number(updated[globalIndex].noAptos || 0) +
            Number(updated[globalIndex].faltantesFacturados || 0));

        updated[globalIndex].cantidadRecibidos = String(
          recalculated >= 0 ? recalculated : 0,
        );
        setRows(updated);
        setMaterialesOrden?.(updated);
        return;
      }

      row.cantidadRecibidos = String(cantidadRecibidosCalc);
      if (value && value.toString().trim() !== "") {
        delete newErrores[`${globalIndex}-${field}`]; // Usamos globalIndex
      }

      if (field === "noAptos") {
        const dependientes = [
          "problemasEnRecepcion",
          "diagnostico",
          "tratamiento",
          "comentariosGenerales",
        ];
        if (
          !value ||
          value.toString().trim() === "" ||
          value.toString().trim() === "0"
        ) {
          dependientes.forEach((campo) => {
            delete newErrores[`${globalIndex}-${campo}`];
          });
        } else {
          dependientes.forEach((campo) => {
            if (!row[campo] || row[campo].toString().trim() === "") {
              newErrores[`${globalIndex}-${campo}`] = true;
            }
          });
        }
      }

      const isManual = row.manual === true;
      const obligatorios = isManual
        ? ["materialId", "piezasPedidas", "costoUnitario"]
        : ["facturados"];

      obligatorios.forEach((campo) => {
        if (!row[campo] || row[campo].toString().trim() === "") {
          newErrores[`${globalIndex}-${campo}`] = true;
        } else {
          delete newErrores[`${globalIndex}-${campo}`];
        }
      });

      setRows(updated);
      setMaterialesOrden?.(updated);
      setErrores(newErrores);
    };

    const deleteRow = (index) => {
      const row = rows[index];
      if (!row.manual) return;
      const updatedRows = rows.filter((_, i) => i !== index);
      setRows(updatedRows);
      setMaterialesOrden?.(updatedRows);
    };

    const addRow = () => {
      const newRow = columns2.reduce((acc, col) => {
        acc[col] = "";
        return acc;
      }, {});
      newRow.manual = true;
      newRow.addedNow = true;
      const updatedRows = [...rows, newRow];
      //console.log("UPDATED ROWS", updatedRows);
      setRows(updatedRows);
      setMaterialesOrden?.(updatedRows);
    };

    useImperativeHandle(ref, () => ({
      addRow,
      deleteRow,
      getCurrentStageData: () => rows,
    }));

    const handleBuscarDescripcion = async (indexInPage, col, value) => {
      const globalIndex = indexOfFirstRow + indexInPage;
      if (!rows[globalIndex]?.manual || col !== "materialId") return;

      const existeDuplicado = rows.some(
        (row, i) =>
          i !== globalIndex && String(row.materialId) === String(value),
      );
      if (existeDuplicado) {
        Swal.fire({
          icon: "warning",
          title: "Material duplicado",
          text: `El material con ID ${value} ya existe en la orden.`,
          confirmButtonColor: "#00205B",
          confirmButtonText: "Aceptar",
        });

        const updated = [...rows];
        updated[globalIndex] = {
          ...updated[globalIndex],
          materialId: "",
          materialDescripcion: "",
        };

        setRows(updated);
        setMaterialesOrden?.(updated);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_PETCOZOO}/reporteRecepcion/getMaterialDescripcion/${value}`,
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_TOKEN}`,
              apikey: `${process.env.REACT_APP_APIKEY}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.data.data.MATERIAL_NOMBRE) {
          // --- CAMBIO: Actualizar sobre el array completo ---
          const updated = [...rows];
          updated[globalIndex] = {
            ...updated[globalIndex],
            materialId: value, // Aseguramos que el ID se mantenga
            materialDescripcion: response.data.data.MATERIAL_NOMBRE,
          };
          setRows(updated);
          setMaterialesOrden?.(updated);
        } else {
          Swal.fire({
            icon: "warning",
            title: "Material no encontrado",
            text: `No existe la descripción para el ID: ${value}`,
            confirmButtonColor: "#00205B",
            confirmButtonText: "Aceptar",
          });
        }
      } catch (error) {
        console.error("Error al buscar descripción:", error);
        // ... tu lógica de catch se mantiene igual ...
        if (error.response) {
          Swal.fire({
            title: error.response.data.error,
            text: "Inténtalo de nuevo",
            icon: "error",
            confirmButtonColor: "#001F5B",
            confirmButtonText: "Aceptar",
          });
        }
      }
    };

    return (
      <div>
        <Cliploader loading={loading} />
        <TableContainer className="container-table-reporte">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col, index) => (
                  <Tooltip
                    key={index}
                    title={col}
                    arrow
                    placement="top"
                    enterDelay={600}
                    leaveDelay={100}>
                    <TableCell id="thead-reporte">{col}</TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {currentRows &&
                currentRows.map((row, rowIndex) => {
                  const globalIndex = indexOfFirstRow + rowIndex;
                  return (
                    <TableRow key={globalIndex}>
                      {columns2
                        .filter((col) => col !== "manual")
                        .map((col, colIndex) => {
                          //console.log("ROW", row);
                          //console.log("COL", [col]);
                          //console.log("COL INDEX", colIndex);
                          const value = row[col] ?? ""; // Evita undefined/null
                          //console.log("VALUE", value);
                          const isManual = row.manual;
                          const tieneError = errores?.[`${globalIndex}-${col}`];

                          // Celdas a bloquear
                          const isDisabled = (() => {
                            if (isManual) {
                              return (
                                col === "materialDescripcion" ||
                                col === "cantidadRecibidos"
                              );
                            }
                            return (
                              col === "materialId" ||
                              col === "piezasPedidas" ||
                              col === "materialDescripcion" ||
                              col === "costoUnitario" ||
                              col == "cantidadRecibidos"
                            );
                          })();

                          // Celda especial: PROBLEMAS EN RECEPCION
                          if (col === "problemasEnRecepcion") {
                            return (
                              <Tooltip
                                key={colIndex}
                                title={value}
                                arrow
                                placement="top"
                                enterDelay={600}
                                leaveDelay={100}>
                                <TableCell className="styles-celdas">
                                  <Select
                                    //disabled={!isDisabled}
                                    error={tieneError}
                                    sx={{
                                      height: "25px",
                                      maxWidth: "80px",
                                      fontSize: "11px",
                                    }}
                                    value={value}
                                    onChange={(e) =>
                                      handleChange(
                                        rowIndex,
                                        col,
                                        e.target.value,
                                      )
                                    }
                                    size="small"
                                    fullWidth>
                                    {catalogoProblms.map((p, i) => (
                                      <MenuItem key={i} value={p.problemaText}>
                                        {p.problemaText}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </TableCell>
                              </Tooltip>
                            );
                          }
                          // Backgroundcolor para cantidad recibidos y no aptos
                          const backgroundColor =
                            col === "noAptos"
                              ? "lightpink"
                              : col === "faltantesFacturados"
                                ? "#F9FC6A"
                                : col === "cantidadRecibidos"
                                  ? "lightgreen"
                                  : "transparent";

                          return (
                            <Tooltip
                              key={colIndex}
                              title={value}
                              arrow
                              placement="top"
                              enterDelay={600}
                              leaveDelay={100}>
                              <TableCell
                                className="styles-celdas"
                                sx={{
                                  //border: "solid 0.1px",
                                  backgroundColor,
                                }}>
                                {isManual && colIndex == 0 && (
                                  <span
                                    style={{
                                      left: 2,
                                      top: 2,
                                      fontSize: "10px",
                                      color: "#FFC107",
                                      fontWeight: "bold",
                                    }}>
                                    ⭐ Manual
                                  </span>
                                )}
                                {isManual && colIndex == 2 && (
                                  <span
                                    style={{
                                      left: 2,
                                      top: 2,
                                      fontSize: "10px",
                                      color: "#FFC107",
                                      fontWeight: "bold",
                                    }}>
                                    Adicionales
                                  </span>
                                )}

                                <TextField
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      fontSize: "11px",
                                      padding: "4px",
                                      backgroundColor: tieneError
                                        ? "#ffebee"
                                        : "transparent",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      "& fieldset": {
                                        borderColor: tieneError
                                          ? "red"
                                          : undefined,
                                      },
                                    },
                                    paddingLeft:
                                      isManual && colIndex === 0
                                        ? "11px"
                                        : "4px",
                                  }}
                                  value={value}
                                  onChange={(e) =>
                                    handleChange(rowIndex, col, e.target.value)
                                  }
                                  onBlur={
                                    isManual &&
                                    col === "materialId" &&
                                    row.materialId
                                      ? () =>
                                          handleBuscarDescripcion(
                                            rowIndex,
                                            col,
                                            value,
                                          )
                                      : undefined
                                  }
                                  disabled={isDisabled}
                                />
                              </TableCell>
                            </Tooltip>
                          );
                        })}
                      <TableCell className="styles-celdas">
                        <div className="div-btn-center">
                          {row.addedNow ? (
                            <svg
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => deleteRow(globalIndex)}
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="black"
                              stroke-width="1"
                              stroke-linecap="round"
                              stroke-linejoin="round">
                              <path d="M4 7h16" />
                              <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                              <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                              <path d="M10 12l4 4m0 -4l-4 4" />
                            </svg>
                          ) : (
                            <span style={{ opacity: 0.3 }}>—</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          {rows.length === 0 && !loading && (
            <Box
              sx={{
                textAlign: "center",
                padding: 2,
                color: "gray",
                border: "1px solid lightgray",
                borderTop: "none",
              }}>
              No hay datos
            </Box>
          )}
        </TableContainer>

        {rows.length > 0 && (
          <Box display="flex" justifyContent="center" mt={1} gap={1}>
            {currentPage > 1 && (
              <Button onClick={() => setCurrentPage(currentPage - 1)}>
                Anterior
              </Button>
            )}
            <span>
              Página {currentPage} de {totalPages}
            </span>
            {currentPage < totalPages && (
              <Button onClick={() => setCurrentPage(currentPage + 1)}>
                Siguiente
              </Button>
            )}
          </Box>
        )}
      </div>
    );
  },
);

export default ExcelEditor;
